import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ReportingContent: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          For Viewers
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Reporting Content
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Help keep IndeeClips safe by reporting videos, channels, or comments that violate community guidelines. This guide explains when and how to report inappropriate content.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What to Report
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Report content that violates IndeeClips policies:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Sexual or adult content</strong></li>
          <li><strong>Violent or graphic content</strong></li>
          <li><strong>Hateful or abusive content</strong> (targeting race, religion, gender, etc.)</li>
          <li><strong>Spam or misleading content</strong></li>
          <li><strong>Copyright infringement</strong></li>
          <li><strong>Child safety violations</strong> (report immediately)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Report a Video
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click three-dot menu (⋮) on video thumbnail or below player</li>
          <li>Select "Report"</li>
          <li>Choose violation type from list</li>
          <li>Provide additional details (optional but helpful)</li>
          <li>Click "Submit Report"</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Report a Channel
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Visit the channel page</li>
          <li>Click three-dot menu in channel header</li>
          <li>Select "Report Channel"</li>
          <li>Describe the pattern of violations</li>
          <li>Submit</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Report a Comment
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Find the comment under a video</li>
          <li>Click three-dot menu next to comment</li>
          <li>Select "Report"</li>
          <li>Choose reason (spam, hate speech, harassment)</li>
          <li>Submit</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What Happens After You Report
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Review queue:</strong> Report enters moderation queue</li>
          <li><strong>Anonymous:</strong> Creator never knows who reported</li>
          <li><strong>Timeline:</strong> Most reports reviewed within 24-48 hours</li>
          <li><strong>Action:</strong> If violation confirmed, content removed or channel penalized</li>
          <li><strong>No notification:</strong> You typically won't be notified of outcome</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          When NOT to Report
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Content you simply don't like (use "Not Interested" instead)</li>
          <li>Opinions you disagree with (unless hateful/abusive)</li>
          <li>Competitor channels (personal disputes aren't violations)</li>
          <li>Clickbait (annoying but not reportable unless extremely misleading)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Urgent Reports
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          For serious violations (child safety, terrorism, immediate harm):
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Report through normal process first</li>
          <li>Email contact@indeeclips.com with "URGENT" in subject line</li>
          <li>Include video URL and description of violation</li>
          <li>For illegal content, also contact local authorities</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          False Reporting Consequences
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Repeatedly filing false reports to harm creators may result in losing reporting privileges or account suspension. Only report genuine violations.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Questions About Reporting?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Contact{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ReportingContent;
